import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ButtonPrimary,
  Card,
  Badge as LibBadge,
  Icon
} from '../components/ComponentLibrary';
import { Sparkles, Brain, Code2, Rocket, Heart, ShieldCheck, Users } from 'lucide-react';
import FeatureIcon from '../components/FeatureIcon'; // Import explicitly if not correctly from Library
import ScrollReveal from '../components/ScrollReveal';
import './AboutNew.css';
import logoImg from '../img/Untitled design (19).png';

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="about-page">
      {/* 1. Hero Section */}
      <ScrollReveal direction="down" duration={0.8}>
        <section className="about-hero">
          <div className="hero-content">
            <span className="hero-tag"><Icon name="rocket" size={16} /> مستقبل التعليم هنا</span>
            <h1 className="hero-heading">أهلاً بك في كود ويف</h1>
            <p className="hero-text">
              نحن لا نعلم البرمجة فحسب، بل نمكن الأطفال من البناء، الابتكار، واختراع المستقبل.
              مهارات هندسية حقيقية مصممة خصيصاً للأجيال القادمة.
            </p>
          </div>
        </section>
      </ScrollReveal>

      <div className="main-area" style={{ padding: '0 24px' }}>

        {/* 2. Mission Statement */}
        <ScrollReveal direction="up" delay={0.2}>
          <div className="mission-container">
            <div className="mission-card fade-in">
              <h2 className="mission-title">مهمتنا <Icon name="sparkles" size={24} /></h2>
              <p className="mission-quote">
                تحويل طفلك من <strong>مستهلك</strong> للتكنولوجيا إلى <strong>مبتكر</strong> لها.
                نوفر بيئة آمنة، ممتعة، واحترافية حيث يلتقي الفضول بالهندسة الحقيقية.
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* 3. What We Teach */}
        <ScrollReveal direction="up" delay={0.2}>
          <h2 className="about-section-title">ما الذي سيتعلمه طفلك؟</h2>
          <div className="learning-grid">
            <ScrollReveal direction="up" delay={0.1} distance={20}>
              <div className="learning-card">
                <FeatureIcon name="lessons" variant="ocean" size="md" priority="primary" className="mb-4" />
                <h3>برمجة حقيقية</h3>
                <p className="meta">Python, JavaScript، وتطوير الويب باستخدام أدوات احترافية مبسطة.</p>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="up" delay={0.2} distance={20}>
              <div className="learning-card">
                <FeatureIcon name="ai" variant="sunshine" size="md" priority="primary" className="mb-4" />
                <h3>الذكاء الاصطناعي</h3>
                <p className="meta">فهم كيفية عمل الذكاء الاصطناعي وبناء نماذج ذكية خاصة بهم.</p>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="up" delay={0.3} distance={20}>
              <div className="learning-card">
                <FeatureIcon name="sparkles" variant="sky" size="md" priority="primary" className="mb-4" />
                <h3>تصميم إبداعي</h3>
                <p className="meta">أساسيات UI/UX والإبداع الرقمي لجعل مشاريعهم تبدو مذهلة.</p>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="up" delay={0.4} distance={20}>
              <div className="learning-card">
                <FeatureIcon name="target" variant="ocean" size="md" priority="primary" className="mb-4" />
                <h3>حل المشكلات</h3>
                <p className="meta">مهارات التفكير الحسابي التي تساعد في الرياضيات والعلوم والحياة.</p>
              </div>
            </ScrollReveal>
          </div>
        </ScrollReveal>

        {/* 4. Kids vs Parents Perspective */}
        <div className="perspective-split">
          {/* Kids Perspective */}
          <ScrollReveal direction="left" delay={0.2}>
            <div className="split-item kids-view">
              <h3 className="split-title">
                <Icon name="sparkles" size={24} /> لماذا يحبنا الأطفال؟
              </h3>
              <ul className="benefit-list">
                <li><Icon name="play" size={18} /> <span>التعلم كأنه لعبة ممتعة</span></li>
                <li><Icon name="achievement" size={18} /> <span>كسب الشارات والشهادات</span></li>
                <li><Icon name="parent" size={18} /> <span>تكوين صداقات في حصص تفاعلية</span></li>
                <li><Icon name="rocket" size={18} /> <span>بناء تطبيقات وألعاب حقيقية</span></li>
              </ul>
            </div>
          </ScrollReveal>

          {/* Parents Perspective */}
          <ScrollReveal direction="right" delay={0.2}>
            <div className="split-item parents-view">
              <h3 className="split-title">
                <Icon name="check" size={24} /> لماذا يثق بنا الآباء؟
              </h3>
              <ul className="benefit-list">
                <li><Icon name="user" size={18} /> <span>مدربون مهندسون متخصصون</span></li>
                <li><Icon name="lock" size={18} /> <span>بيئة تعلم آمنة ومراقبة</span></li>
                <li><Icon name="progress" size={18} /> <span>تقارير شهرية عن تقدم الطالب</span></li>
                <li><Icon name="mail" size={18} /> <span>تواصل مباشر مع المرشدين</span></li>
              </ul>
            </div>
          </ScrollReveal>
        </div>

        {/* 5. The Mentors */}
        <section className="mentors-section">
          <ScrollReveal direction="up" delay={0.2}>
            <h2 className="about-section-title">تعرف على المدربين</h2>
            <p style={{ textAlign: 'center', color: 'var(--cw-gray-600)', marginBottom: '40px' }}>
              نحن لسنا مجرد مدرسين، بل مهندسون في كبرى الشركات نعشق نقل المعرفة.
            </p>
          </ScrollReveal>
          <div className="mentors-grid">
            <ScrollReveal direction="up" delay={0.1} distance={30}>
              <div className="mentor-card">
                <div className="mentor-avatar">
                  <Icon name="user" size={48} />
                </div>
                <h3>م. عبدو قمر</h3>
                <LibBadge variant="ocean">كبير مطورين</LibBadge>
                <p className="meta">خبرة 10 سنوات في هندسة البرمجيات.</p>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="up" delay={0.2} distance={30}>
              <div className="mentor-card">
                <div className="mentor-avatar">
                  <Icon name="user" size={48} />
                </div>
                <h3>م. سارة</h3>
                <LibBadge variant="sunshine">متخصصة ذكاء اصطناعي</LibBadge>
                <p className="meta">خبيرة في Python وتعلم الآلة.</p>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="up" delay={0.3} distance={30}>
              <div className="mentor-card">
                <div className="mentor-avatar">
                  <Icon name="sparkles" size={48} />
                </div>
                <h3>م. محمد ياسر</h3>
                <LibBadge variant="ocean">مسؤول الابتكار</LibBadge>
                <p className="meta">يجعل البرمجة مرئية وممتعة للجميع.</p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* 6. Final CTA */}
        <ScrollReveal direction="up" delay={0.2}>
          <div className="about-cta">
            <h2 className="cta-heading">جاهز لتبدأ الرحلة؟ <Icon name="sparkles" size={32} /></h2>
            <p style={{ fontSize: '1.2rem', marginBottom: '32px', opacity: 0.9 }}>
              انضم لمئات الطلاب الذين يبنون المستقبل اليوم.
            </p>
            <ButtonPrimary
              size="lg"
              onClick={() => navigate('/dashboard')}
              className="cta-button"
            >
              عرض خطط الاشتراك <Icon name="rocket" size={20} />
            </ButtonPrimary>
          </div>
        </ScrollReveal>

      </div>
    </div>
  );
}